/**
 * Cliente Firebase Auth para painéis administrativos (médico/admin/suporte).
 * --------------------------------------------------------------
 * Onda 1B do hardening (junho/2026). Substitui senhas estáticas
 * REACT_APP_*_PASSWORD por login formal com email/senha + TOTP/2FA.
 *
 * Roles (custom claims, atribuídos via functions/scripts/_grant_role.js):
 *   - admin   : painéis ops (health, controle, cupons, ux-monitor)
 *   - doctor  : painel /medico
 *   - support : painel /suporte
 *
 * Uso:
 *   import { signInAdmin, signOutAdmin, observeAdmin, getAdminIdToken,
 *            enrollTotp, finishTotpEnrollment, completeMfaSignIn,
 *            roleHasAccess } from './adminAuth';
 *
 *   const unsub = observeAdmin((state) => { ... });
 *
 *   try {
 *     const result = await signInAdmin(email, password);
 *     if (result.mfaRequired) {
 *       // pedir codigo TOTP do app autenticador, depois completeMfaSignIn(...)
 *     }
 *   } catch (e) { ... }
 *
 *   const idToken = await getAdminIdToken();
 *   fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
 */

import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  getMultiFactorResolver,
} from 'firebase/auth';
import { getFirebaseAppInstance } from '../telemetry/firebase';

let authInstance = null;

const getAuthInstance = () => {
  if (authInstance) return authInstance;
  const app = getFirebaseAppInstance();
  if (!app) return null;
  authInstance = getAuth(app);
  // Persistencia padrao (browserLocalPersistence) — usuario fica logado
  // entre reloads. Em paineis administrativos esse comportamento e
  // esperado; quando quiser logoff total, chame signOutAdmin().
  return authInstance;
};

/**
 * Inicia sessao com email/senha. Pode lancar erro com `mfaRequired=true`
 * quando o usuario tem TOTP configurado (vide finishMfaSignIn).
 */
export async function signInAdmin(email, password) {
  const auth = getAuthInstance();
  if (!auth) throw new Error('Firebase nao configurado no build.');
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const tokenResult = await cred.user.getIdTokenResult(true);
    return {
      mfaRequired: false,
      user: cred.user,
      claims: tokenResult.claims || {},
    };
  } catch (err) {
    if (err && err.code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(auth, err);
      const totpHint = (resolver.hints || []).find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      return {
        mfaRequired: true,
        resolver,
        totpHint: totpHint || null,
      };
    }
    throw err;
  }
}

/**
 * Completa login depois de um sign-in que retornou mfaRequired=true.
 * `code` = 6 digitos do app autenticador (Google Authenticator, 1Password, etc.).
 */
export async function completeMfaSignIn(resolver, totpHint, code) {
  if (!resolver || !totpHint) throw new Error('MFA invalido: faltam resolver/hint.');
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, String(code).trim());
  const cred = await resolver.resolveSignIn(assertion);
  const tokenResult = await cred.user.getIdTokenResult(true);
  return { user: cred.user, claims: tokenResult.claims || {} };
}

/**
 * Inicia enrollment TOTP: gera secret e retorna QR + chave manual.
 * O usuario escaneia no app autenticador, anota o codigo, e chama
 * finishTotpEnrollment() com o codigo gerado.
 */
export async function enrollTotp(user) {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const appName = 'Consulta+';
  return {
    secret, // mantenha em memoria para finishTotpEnrollment
    qrCodeUrl: secret.generateQrCodeUrl(user.email || user.uid, appName),
    secretKey: secret.secretKey,
  };
}

export async function finishTotpEnrollment(user, secret, code, displayName = 'Authenticator') {
  if (!(secret instanceof TotpSecret)) throw new Error('Secret invalido.');
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, String(code).trim());
  await multiFactor(user).enroll(assertion, displayName);
  return true;
}

export async function signOutAdmin() {
  const auth = getAuthInstance();
  if (!auth) return;
  await signOut(auth).catch(() => {});
}

/**
 * Observer global. Retorna unsubscribe.
 * Notifica com { user, claims, role } a cada mudanca.
 */
export function observeAdmin(callback) {
  const auth = getAuthInstance();
  if (!auth) {
    callback({ user: null, claims: {}, role: null, ready: true });
    return () => {};
  }
  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      callback({ user: null, claims: {}, role: null, ready: true });
      return;
    }
    try {
      const tokenResult = await user.getIdTokenResult(false);
      const claims = tokenResult.claims || {};
      callback({
        user,
        claims,
        role: claims.role || null,
        ready: true,
      });
    } catch {
      callback({ user, claims: {}, role: null, ready: true });
    }
  });
}

/**
 * Obtem o ID token mais recente. Retorna null em caso de erro/no-user.
 */
export async function getAdminIdToken({ forceRefresh = false } = {}) {
  const auth = getAuthInstance();
  if (!auth || !auth.currentUser) return null;
  try {
    return await auth.currentUser.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}

/**
 * Helper de RBAC: verifica se a role atual (string) pode acessar uma rota
 * que aceita uma lista de roles.
 */
export function roleHasAccess(currentRole, allowedRoles) {
  const r = String(currentRole || '').toLowerCase();
  const list = (allowedRoles || []).map((x) => String(x).toLowerCase());
  return list.includes(r);
}

/**
 * Verifica se o usuario tem MFA enrolled.
 */
export function hasMfaEnrolled(user) {
  try {
    return multiFactor(user).enrolledFactors.length > 0;
  } catch {
    return false;
  }
}
